/*
  La Matcha — contact forms (business quote form and contact form).

    <contact-form>  wraps a Shopify contact form. Nothing is checked until the first submit;
                    after that every field is checked again while the customer types.

  Markup:
    [data-rule]          control to validate: required | email | phone | message | checked.
                         Its error element has the id "<control id>-error" and starts hidden.
    [data-topic]         topic radios; the ones with [data-order-number] ask for an order number
    [data-order-field]   wrapper of the order number field
    [data-form-status]   success panel or error summary rendered after the page reloads

  The form itself is sent as a normal POST. Without JavaScript the browser validates it through
  required, type and minlength.
*/
(() => {
  if (customElements.get('contact-form')) return;

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const RULES = {
    required: (control) => control.value.trim() !== '',
    email: (control) => EMAIL.test(control.value.trim()),
    phone: (control) => control.value.replace(/\D/g, '').length >= 10,
    message: (control) => control.value.trim().length > 2,
    checked: (control) => control.checked,
  };

  class ContactForm extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.form = this.querySelector('form');
      this.revealStatus();
      if (!this.form) return;

      this.controls = [...this.form.querySelectorAll('[data-rule]')];
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.tried = false;
      if (this.controls.length) this.form.noValidate = true;

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.form.addEventListener('input', (event) => this.onEdit(event));
      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-topic]')) this.syncOrderField();
        this.onEdit(event);
      });
      // Coming back through the browser history must not leave the button in its loading state.
      this.onPageShow = () => this.setLoading(false);
      window.addEventListener('pageshow', this.onPageShow);
      this.syncOrderField();
    }

    disconnectedCallback() {
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    }

    onSubmit(event) {
      this.tried = true;
      const invalid = this.controls.filter((control) => !this.check(control));
      if (invalid.length) {
        event.preventDefault();
        invalid[0].focus();
        return;
      }
      this.setLoading(true);
    }

    onEdit(event) {
      const control = event.target;
      if (!control.matches || !control.matches('[data-rule]')) return;
      // A field Shopify rejected is checked again as soon as it is edited.
      if (this.tried || control.getAttribute('aria-invalid') === 'true') this.check(control);
    }

    /* Applies the rule of one control and shows or hides its error. Returns true when valid. */
    check(control) {
      const rule = RULES[control.dataset.rule];
      const valid = control.disabled || !rule || rule(control);
      const error = document.getElementById(`${control.id}-error`);
      if (valid) {
        control.removeAttribute('aria-invalid');
        control.removeAttribute('aria-describedby');
      } else {
        control.setAttribute('aria-invalid', 'true');
        if (error) control.setAttribute('aria-describedby', error.id);
      }
      if (error) error.hidden = valid;
      return valid;
    }

    syncOrderField() {
      const field = this.form.querySelector('[data-order-field]');
      if (!field) return;
      const topic = this.form.querySelector('[data-topic]:checked');
      const show = !topic || topic.hasAttribute('data-order-number');
      field.hidden = !show;
      field.querySelectorAll('input').forEach((input) => {
        input.disabled = !show;
      });
    }

    setLoading(state) {
      if (this.submitButton) this.submitButton.classList.toggle('is-loading', state);
    }

    /* After the reload: bring the result into view and move focus to it so it is announced.
       Runs once the page has loaded, so late layout changes (fonts, images) cannot move the
       target away, and jumps instead of animating for the same reason. */
    revealStatus() {
      const status = this.querySelector('[data-form-status]');
      if (!status || (window.LM && window.LM.designMode)) return;
      const reveal = () => {
        const top = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        const rect = status.getBoundingClientRect();
        // The form wrapper is the scroll target: the panel itself is still mid-animation.
        if (rect.top < top || rect.bottom > window.innerHeight) this.scrollIntoView({ block: 'start', behavior: 'instant' });
        status.focus({ preventScroll: true });
      };
      if (document.readyState === 'complete') reveal();
      else window.addEventListener('load', reveal, { once: true });
    }
  }

  customElements.define('contact-form', ContactForm);
})();
